import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { episodeScriptApi } from '../../services/user';
import { useGlobalLoading } from '../../stores/globalLoadingStore';
import { useToast } from '../../stores/toastStore';
import { useDialog } from '../useDialog';
import { useScriptErrorHandler } from '../useScriptErrorHandler';

interface UseScriptActionsOptions {
  scriptId: string | undefined;
  currentStep: number;
  currentStepName: string | undefined;
  actionVerb: string;
  setIsGenerating: (v: boolean) => void;
  setIsFailed: (v: boolean) => void;
  setErrorMessage: (v: string | undefined) => void;
  markStepInProgress: (step: number) => void;
  navigateToStep: (step: number) => void;
  refreshScript: () => Promise<void>;
  saveStepData: (step: number) => Promise<void>;
  updateStepData: (step: number, data: any) => void;
  backupStepDebounced: (step: number, data: any) => void;
  clearStepBackup: (step: number) => void;
  setIsEditing: (v: boolean) => void;
  currentGenerationStep?: number;
}

export function useScriptActions({
  scriptId, currentStep, currentStepName, actionVerb,
  setIsGenerating, setIsFailed, setErrorMessage,
  markStepInProgress, navigateToStep, refreshScript,
  saveStepData, updateStepData, backupStepDebounced, clearStepBackup,
  setIsEditing, currentGenerationStep,
}: UseScriptActionsOptions) {
  const navigate = useNavigate();
  const dialog = useDialog();
  const toast = useToast();
  const { showLoading, hideLoading } = useGlobalLoading();
  const { executeWithRetry, getErrorMessage, clearError } = useScriptErrorHandler();

  const handleRegenerateStep = useCallback(async () => {
    if (!scriptId) return;
    const confirmed = await dialog.confirm(`确定要重新${actionVerb}「${currentStepName}」吗？`, '确认操作');
    if (!confirmed) return;
    try {
      setIsGenerating(true);
      setIsFailed(false);
      setErrorMessage(undefined);
      clearError();
      showLoading('正在处理...');
      // 走 redoStep：通过任务队列重做指定步骤，依赖后端 isStepCompleted 守卫，
      // 只重做该步及未完成的下游。适用于剧本已 COMPLETED 后单独重做某步（如 Step 6 分镜）。
      markStepInProgress(currentStep);
      await executeWithRetry(() => episodeScriptApi.redoStep(scriptId, currentStep), {
        onRetry: (count) => toast.info(`正在重试 (${count}/3)...`)
      });
      await refreshScript();
      toast.success(`已开始重做${currentStepName || '当前步骤'}`);
    } catch (err: any) {
      setIsFailed(true);
      setErrorMessage(getErrorMessage());
      toast.error(err.message || '操作失败');
    } finally {
      hideLoading();
      // 不立刻 setIsGenerating(false)，等待 WebSocket 推进步骤状态
    }
  }, [scriptId, actionVerb, currentStepName, currentStep]);

  const handleRetry = useCallback(async () => {
    if (!scriptId) return;
    try {
      setIsFailed(false);
      setErrorMessage(undefined);
      setIsGenerating(true);
      showLoading('正在重新生成脚本...');
      await episodeScriptApi.retryGeneration(scriptId);
      const retryFromStep = currentGenerationStep || 1;
      markStepInProgress(retryFromStep);
      if (retryFromStep !== currentStep) navigateToStep(retryFromStep);
      await refreshScript();
      toast.success('已重新开始生成');
    } catch (err: any) {
      setIsFailed(true);
      setErrorMessage(err.message || '重试失败');
      toast.error(err.message || '重试失败');
      setIsGenerating(false);
    } finally {
      hideLoading();
    }
  }, [scriptId, currentGenerationStep, currentStep]);

  const handleForceRetry = useCallback(async () => {
    if (!scriptId) return;
    const confirmed = await dialog.confirm('确定要强制继续吗？这将取消当前任务并从当前步骤重新开始。', '强制继续');
    if (!confirmed) return;
    try {
      setIsFailed(false);
      setErrorMessage(undefined);
      setIsGenerating(true);
      showLoading('正在强制继续生成...');
      await episodeScriptApi.forceRetryGeneration(scriptId);
      const retryFromStep = currentGenerationStep || 1;
      markStepInProgress(retryFromStep);
      if (retryFromStep !== currentStep) navigateToStep(retryFromStep);
      await refreshScript();
      toast.success('已强制继续生成');
    } catch (err: any) {
      setIsFailed(true);
      setErrorMessage(err.message || '强制重试失败');
      toast.error(err.message || '强制重试失败');
      setIsGenerating(false);
    } finally {
      hideLoading();
    }
  }, [scriptId, currentGenerationStep, currentStep]);

  const handleStepDataChange = useCallback((data: any) => {
    updateStepData(currentStep, data);
    backupStepDebounced(currentStep, data);
  }, [currentStep, updateStepData, backupStepDebounced]);

  const handleStepDataSave = useCallback(async () => {
    try {
      await executeWithRetry(() => saveStepData(currentStep), {
        onRetry: (count) => toast.info(`保存重试中 (${count}/3)...`)
      });
      clearStepBackup(currentStep);
      setIsEditing(false);
      toast.success('保存成功');
    } catch (err: any) {
      toast.error(err.message || '保存失败');
    }
  }, [currentStep, saveStepData, clearStepBackup, setIsEditing]);

  const handleBack = useCallback((projectId?: string) => {
    if (projectId) navigate(`/project/${projectId}?tab=episodes`);
    else navigate(-1);
  }, [navigate]);

  const handleViewVideo = useCallback((episodeId?: string) => {
    if (episodeId) navigate(`/episode/${episodeId}/editor`);
  }, [navigate]);

  const handleTabChange = useCallback((tab: 'script' | 'editor', episodeId?: string) => {
    if (tab === 'editor' && episodeId) navigate(`/episode/${episodeId}/editor`);
  }, [navigate]);

  return {
    handleRegenerateStep,
    handleRetry,
    handleForceRetry,
    handleStepDataChange,
    handleStepDataSave,
    handleBack,
    handleViewVideo,
    handleTabChange,
  };
}
